import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Bot, Send, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { financeDataService } from '../services/financeDataService';
import { auth } from '../config/firebase-config';
import 'bootstrap/dist/css/bootstrap.min.css';

const API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

function generateMessageId() {
  return Math.random().toString(36).substring(2, 15);
}

function ChatMessage({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`d-flex gap-3 ${isUser ? 'flex-row-reverse' : ''} mb-3`}>
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center ${isUser ? 'bg-primary text-white' : 'bg-light'}`}
        style={{ width: '40px', height: '40px' }}
      >
        {isUser ? <MessageSquare className="w-50 h-50" /> : <Bot className="w-50 h-50 text-secondary" />}
      </div>
      <div className={`w-75 ${isUser ? 'text-end' : ''}`}>
        <div className={`p-3 rounded ${isUser ? 'bg-primary text-white' : 'bg-light text-dark'}`}>
          {message.content}
        </div>
        <small className="text-muted mt-1 d-block">{format(message.timestamp, 'HH:mm')}</small>
      </div>
    </div>
  );
}

function ChatInput({ onSendMessage }) {
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-top p-3 bg-white">
      <div className="d-flex gap-3">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask about your expenses..."
          className="form-control"
          rows={1}
        />
        <button
          onClick={handleSend}
          className="btn btn-primary d-flex align-items-center justify-content-center"
        >
          <Send className="w-100 h-100" />
        </button>
      </div>
    </div>
  );
}

function App() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [financialContext, setFinancialContext] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeChat = async () => {
      try {
        setIsInitializing(true);
        
        // Wait for auth state to be ready
        await new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
          });
        });

        // Get current user
        const user = auth.currentUser;
        if (!user) {
          console.log("No user found, redirecting to login");
          navigate('/login');
          return;
        }

        // Get fresh token with retry mechanism
        let token;
        try {
          token = await user.getIdToken(true);
          localStorage.setItem('firebaseToken', token);
        } catch (tokenError) {
          console.error("Error getting token:", tokenError);
          throw new Error("Failed to get authentication token");
        }

        // Fetch financial data with retry mechanism
        let retries = 3;
        let data;
        while (retries > 0) {
          try {
            data = await financeDataService.getUserFinancialContext();
            break;
          } catch (error) {
            retries--;
            if (retries === 0) throw error;
            await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
          }
        }

        setFinancialContext(data);

        // Create initial greeting
        const initialMessage = {
          id: generateMessageId(),
          role: 'assistant',
          content: `Hello! I'm your financial assistant. I've analyzed your recent transactions.${
            data?.transactions?.length > 0 
              ? ` You have ${data.transactions.length} recent transactions.` 
              : ' I see you haven\'t recorded any transactions yet.'
          } How can I help you today?`,
          timestamp: new Date(),
        };

        setMessages([initialMessage]);
      } catch (error) {
        console.error('Error initializing chat:', error);
        
        // Show specific error message based on error type
        let errorMessage = 'I apologize, but I\'m having trouble accessing your financial data. ';
        if (error.message.includes('token')) {
          errorMessage += 'Please try logging in again.';
        } else if (error.message.includes('network')) {
          errorMessage += 'Please check your internet connection.';
        } else {
          errorMessage += 'Please make sure you\'re logged in and try again.';
        }

        setMessages([{
          id: generateMessageId(),
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date(),
        }]);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeChat();
  }, [navigate]);

  const generateFinancialSummary = (data) => {
    const { transactions, profile } = data;
    
    // Calculate key metrics
    const totalExpenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalIncome = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);

    const topCategories = getTopSpendingCategories(transactions);
    
    return `
      Total Income: $${totalIncome.toFixed(2)}
      Total Expenses: $${totalExpenses.toFixed(2)}
      Net Balance: $${(totalIncome - totalExpenses).toFixed(2)}
      Top spending categories: ${topCategories.join(', ')}
    `.trim();
  };

  const getTopSpendingCategories = (transactions) => {
    const categoryTotals = transactions
      .filter(t => t.type === 'expense')
      .reduce((acc, t) => {
        acc[t.category] = (acc[t.category] || 0) + t.amount;
        return acc;
      }, {});

    return Object.entries(categoryTotals)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([category]) => category);
  };

  const handleSendMessage = async (message) => {
    try {
      setIsLoading(true);
      
      // Check authentication with retry mechanism
      const user = auth.currentUser;
      if (!user) {
        console.log("No user found during message send, attempting to refresh");
        await auth.signOut();
        navigate('/login');
        return;
      }

      let token;
      try {
        token = await user.getIdToken(true);
        localStorage.setItem('firebaseToken', token);
      } catch (tokenError) {
        console.error("Error refreshing token:", tokenError);
        navigate('/login');
        return;
      }

      // Add user message to chat
      const userMessage = {
        id: generateMessageId(),
        content: message,
        role: 'user',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, userMessage]);

      // Generate AI response with timeout
      const response = await Promise.race([
        generateResponse(message),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Request timeout')), 30000)
        )
      ]);
      
      const aiMessage = {
        id: generateMessageId(),
        content: response,
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: generateMessageId(),
        content: error.message === 'Request timeout' 
          ? 'Sorry, the request took too long. Please try again.'
          : 'Sorry, I encountered an error processing your message. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateResponse = async (message) => {
    try {
      if (!API_KEY) {
        throw new Error('API Key is missing');
      }

      const systemMessage = `You are a financial assistant with access to the user's financial data. 
        Current financial context:
        ${JSON.stringify(financialContext, null, 2)}
        
        Provide personalized financial advice and insights based on this data.
        Be specific when referring to their spending patterns and suggest actionable improvements.
        Give answers in short and systematic manner`;

      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          messages: [
            { role: 'system', content: systemMessage },
            { role: 'user', content: message },
          ],
          model: 'gpt-3.5-turbo',
          temperature: 0.7,
          max_tokens: 500,
        },
        {
          headers: {
            Authorization: `Bearer ${API_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.choices[0]?.message?.content || 'Sorry, I could not generate a response.';
    } catch (error) {
      console.error('Error generating response:', error);
      return 'Sorry, there was an error processing your request. Please try again later.';
    }
  };

  const handleGoHome = () => {
    navigate('/home'); // Assumes you're using React Router for navigation
  };

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="card shadow w-100" style={{ maxWidth: '600px', minWidth: '300px', width: '90vw', height: "100vh" }}>
        <div className="card-header" style={{ backgroundColor: 'rgb(16, 185, 119)', color: 'white' }}>
          <div className="d-flex align-items-center justify-content-between">
            <button 
              onClick={handleGoHome} 
              className="btn btn-light p-1 d-flex align-items-center" 
              aria-label="Go Home"
            >
              <ArrowLeft className="text-muted" />
            </button>
            <div className="text-center flex-grow-1">
              <h5 className="mb-0">Expense Assistant</h5>
              <small className="text-muted">Ask me about your expenses</small>
            </div>
          </div>
        </div>
        <div
          className="card-body overflow-auto"
          style={{ height: 'calc(100vh - 160px)', padding: '15px 10px' }}
        >
          {isInitializing ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}
              {isLoading && (
                <div className="d-flex justify-content-center mt-3">
                  <div className="text-muted">Typing...</div>
                </div>
              )}
            </>
          )}
        </div>
        <ChatInput onSendMessage={handleSendMessage} />
      </div>
    </div>
  );
}

export default App;